import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../../layout';
import { setNotification } from '../../../../redux/cache-slice';
import { updateCustomerData } from '../../../../redux/leaseApp/lease-app-slice';
import { useAppDispatch } from '../../../../redux/store';
import { RootState } from '../../../../redux/types';
import { APIPaths } from '../../../../types/instant-lease-api';
import { getText } from '../../../../utils/getter';
import { errorLogging } from '../../../../utils/logging';
import Navigation from '../../../../utils/navigation';
import RadioContainer from '../../../b2b/components/radio-container/radio-container';
import Radio from '../../../b2b/components/radio/radio';
import ButtonGroup from '../../../b2b/molecules/button-group/button-group';
import CoApplicant from '../co-applicant/co-applicant';

const HasCoApplicant: React.FC = () => {
  const { activeApplication } = useSelector(
    (state: RootState) => state.leaseApp,
  );
  const { accessToken } = useSelector((state: RootState) => state.auth);

  const navigate = useNavigate();
  const { nextRoute } = new Navigation();
  const dispatch = useAppDispatch();

  const leaseApplicationId = activeApplication?.uuid || '';
  const signers = activeApplication?.customer?.private?.signers;

  // derive initial state once
  const initialHasCoApplicant = useMemo<boolean | undefined>(() => {
    if (!signers || signers.length === 0) return undefined;
    return signers.length > 1;
  }, [signers]);

  const [hasCoApplicant, setHasCoApplicant] = useState<boolean | undefined>(
    initialHasCoApplicant,
  );

  const updateCoApplicant = async (value: boolean) => {
    try {
      await dispatch(
        updateCustomerData({
          accessToken,
          leaseApplicationId,
          inputValue: { is_co_applicant_present: value },
          path: APIPaths.UPDATE_MAIN_SIGNER,
        }),
      );
    } catch (e) {
      errorLogging(e as Error);
      dispatch(
        setNotification({
          notificationVisibility: true,
          notificationHasError: true,
          notificationTitle: 'Das tut uns leid',
          notificationBody: getText('error_unable_to_process_data'),
        }),
      );
    }
  };

  const handleSubmit = async () => {
    if (hasCoApplicant === false) {
      await updateCoApplicant(false);
    }
    navigate(nextRoute());
  };

  const handleSelection = (value: boolean) => async () => {
    setHasCoApplicant(value);
    await updateCoApplicant(value);
  };

  return (
    <Layout
      heading='Möchtest du einen Mitantragsteller hinzuzufügen?'
      stepStatus
      subHeading={getText('has_partner_subTitle')}
      className='choice-modal'
    >
      <div
        className='template-body'
        style={{ display: 'flex', flexDirection: 'column' }}
      >
        <p className='content-bold' style={{ margin: '24px 0 16px 0' }}>
          Jetzt Mitantragssteller hinzufügen:
        </p>

        <div
          className='template-body'
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginTop: '24px',
          }}
        >
          <RadioContainer>
            <Radio
              checked={hasCoApplicant === true}
              onChange={handleSelection(true)}
            >
              <p className='content-normal'>Ja</p>
            </Radio>

            <Radio
              checked={hasCoApplicant === false}
              onChange={handleSelection(false)}
            >
              <p className='content-normal'>Nein</p>
            </Radio>
          </RadioContainer>
        </div>

        {hasCoApplicant && <CoApplicant hasCoApplicant />}
      </div>

      {!hasCoApplicant && (
        <ButtonGroup
          className='back-next medium'
          type='back-next'
          data-testid='disabled-forward-button'
          buttonOneProps={{
            type: 'button',
            disabled: hasCoApplicant === undefined,
            onClick: handleSubmit,
            name: 'form-submit',
            dataTestId: 'next',
            children: 'weiter',
          }}
        />
      )}
    </Layout>
  );
};

export default HasCoApplicant;
